import { useInfiniteQuery } from "react-query";
import { useHeaders } from "hooks/api";
import { getCreativeHistory, CreativeHistoryRes, Status, UserRes, AuditStatusRes, AuditRes } from "api";

export interface CreativeHistory {
    audits: AuditStatusRes[];
    source: string | AuditStatusRes;
    id: number;
    user: UserRes;
    status: Status;
    sourceType?: string;
    endTime: string;
}

export interface GroupedCreativeHistory {
    date: string;
    history: CreativeHistory[];
}

interface HistoryPage {
    total: number;
    page: GroupedCreativeHistory[];
}

const OFFSET = 25;

export const useCreativeHistory = (materialSpecId?: number, materialId?: number) => {
    const headers = useHeaders();

    const buildAudit = (history: AuditRes) => {
        const [, time] = history.endtime.split(" ");

        return {
            id: history.id,
            audits: history.audits,
            status: history.status,
            user: history.user,
            source: history.source,
            sourceType: history.source_enum,
            endTime: time,
        };
    };

    const buildHistoryViewModel = async (res: Promise<CreativeHistoryRes | undefined>): Promise<HistoryPage> => {
        const history = await res;

        if (!history) {
            return {
                total: 0,
                page: [],
            };
        }

        return {
            total: history.totalSize,
            page: history.audits.reduce<GroupedCreativeHistory[]>((groupedHistory, audit) => {
                /**
                 * TODO: Server returns formatted date based on its own configuration e.g. 04.02.2021 15:58:10
                 * date and time should be always separated with whitespace. Temporary solution assumes that date and time
                 * will be always separated with whitespace. CHANGE THAT when server will start sending dates in correct format
                 */
                const [modificationDate] = audit.endtime.split(" ");

                if (!groupedHistory.length) {
                    const [date] = audit.endtime.split(" ");

                    return [
                        {
                            date,
                            history: [buildAudit(audit)],
                        },
                    ];
                }

                // check if group with current date already exists
                const existingAuditGroup = groupedHistory.find(({ date }) => {
                    const [endDate] = date.split(" ");
                    return endDate === modificationDate;
                });

                if (existingAuditGroup) {
                    return groupedHistory.map((group) => {
                        const [endDate] = group.date.split(" ");
                        const [existingEndDate] = existingAuditGroup.date.split(" ");

                        if (endDate === existingEndDate) {
                            group.history.push(buildAudit(audit));

                            return group;
                        }

                        return group;
                    });
                }

                groupedHistory.push({ date: modificationDate, history: [buildAudit(audit)] });

                return groupedHistory;
            }, []),
        };
    };

    return useInfiniteQuery(
        ["creativeHistory", materialId],
        (key, id, offset = 0) => {
            return buildHistoryViewModel(getCreativeHistory(headers, offset as number, materialSpecId, materialId));
        },
        {
            getFetchMore: (lastPage, allPages) => {
                const itemsLength = allPages.reduce(
                    (pages, { page }) => pages + page.reduce((items, { history }) => items + history.length, 0),
                    0
                );

                return itemsLength !== lastPage.total ? allPages.length * OFFSET : false;
            },
        }
    );
};
